import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  styled,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTrips } from 'src/hooks/useTrips';
import { useRecommendedTrips } from 'src/hooks/useRecommendedTrips';
import useCompactView from 'src/hooks/useCompactView';
import { useFavoriteTrips } from 'src/providers/FavoriteTripsContext/useFavoriteTrips';
import { WizardTrackingProvider } from 'src/providers/WizardTrackingProvider/WizardTrackingProvider';
import {
  createWizardTracking,
  WizardResultsAccess,
  WizardVersions,
} from 'src/utils/WizardTracking';
import WizardResultCard from 'src/components/WizardResultCard/WizardResultCard';
import TripSuggester from 'src/components/TripSuggester/TripSuggester';
import { SyncFavoriteTripsBanner } from '../FavoriteTrips/SyncFavoriteTripsBanner';
import WizardBanner from 'src/components/Wizard/WizardBanner/WizardBanner';
import Wizard from '../Wizard/Wizard';
import FlashpackButton from 'src/components/FlashpackButton/FlashpackButton';
import { hasTakenPersonalityTest } from 'src/utils/personalityTest';
import { TripDeparture } from 'src/types/gql-generated-types';
import { findDeparturesForWizard } from 'src/utils/wizardDepartures';
import { useLocale } from 'src/hooks/useLocale';

const dataLayer = window.dataLayer || [];

type TabHash = 'recently-viewed' | 'favorites' | 'recommendations';

interface TabData {
  label: string;
  icon: React.ReactElement;
  component: React.ReactElement;
  hash: TabHash;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const MyTrips: React.FC<{ defaultTab?: TabHash }> = ({
  defaultTab = 'recently-viewed',
}) => {
  const [activeTab, setActiveTab] = useState(
    tabData.findIndex((tab) => tab.hash === defaultTab) || 0,
  );
  const theme = useTheme();

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) as TabHash;
      const tabIndex = tabData.findIndex((tab) => tab.hash === hash);
      if (tabIndex !== -1) setActiveTab(tabIndex);
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const switchTab = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    window.location.hash = tabData[newValue].hash;
  };

  return (
    <Box sx={{ maxWidth: 1260, margin: '0 auto', width: '100%', padding: '0 16px' }}>
      <Box sx={{ paddingTop: '24px' }}>
        <FlashpackTabs
          value={activeTab}
          onChange={switchTab}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabData.map((tab) => (
            <FlashpackTab
              key={tab.label}
              disableRipple
              icon={tab.icon}
              iconPosition="start"
              sx={{ ...theme.typography['Button'] }}
              label={tab.label}
            />
          ))}
        </FlashpackTabs>
      </Box>
      {tabData.map((tab, index) => (
        <TabPanel key={tab.label} value={activeTab} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Box>
  );
};

const RecentlyViewedTrips: React.FC = () => {
  const cachedTrips = window.$FP?.getMostRecentlyViewedTrips() || [];
  const tripCodes = [...new Set(cachedTrips.map((trip) => trip.tripCode))];

  return (
    <Stack pt={3}>
      {tripCodes.length > 0 ? (
        <TripSuggester tripCodes={tripCodes} />
      ) : (
        <Typography variant="Body M">
          We couldn&apos;t find any recently viewed trips
        </Typography>
      )}
    </Stack>
  );
};

const FavoriteTrips: React.FC = () => {
  const { addFavoriteTrip, favorites } = useFavoriteTrips();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const addToFavoritesParam = urlParams.get('addToFavorites');
    if (addToFavoritesParam) {
      const tripCodesToAdd = addToFavoritesParam.split(',');
      tripCodesToAdd.forEach((code) => addFavoriteTrip(code));
      // Remove the parameter from the URL
      urlParams.delete('addToFavorites');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [addFavoriteTrip]);

  // send event to dataLayer when favorites are viewed
  useEffect(() => {
    if (favorites.length > 0) {
      dataLayer.push({
        event: 'view-favorite-trips',
        tripCodes: favorites,
      });
    }
  }, [favorites]);

  return (
    <Stack gap={2} pt={3}>
      <SyncFavoriteTripsBanner numberOfFavorites={favorites.length} />
      {favorites.length > 0 ? (
        <TripSuggester tripCodes={favorites} />
      ) : (
        <Typography variant="Body M">
          We couldn&apos;t find any favorited trips
        </Typography>
      )}
    </Stack>
  );
};

const RecommendedTrips: React.FC = () => {
  const { data: trips } = useTrips();
  const { wizardResults } = useRecommendedTrips(3);
  const isCompactView = useCompactView();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const { currency, language } = useLocale();

  const tripsToDisplay = useMemo(
    () =>
      wizardResults?.tripCodes
        .map((code) => trips?.find((trip) => trip.code === code))
        .filter((trip) => trip !== undefined) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trips],
  );

  const wizardVersion = wizardResults?.wizardVersion || WizardVersions.V4;
  const wizardTracking = createWizardTracking({
    instanceId: wizardResults?.wizardInstanceId || '',
    version: wizardVersion,
  });

  const accessedFrom =
    (new URLSearchParams(window.location.search).get('from') as WizardResultsAccess) ||
    '';
  const shouldPersonalize =
    hasTakenPersonalityTest() && wizardVersion === WizardVersions.V5;

  const [departures, setDepartures] = useState<
    { tripCode: string; departures: TripDeparture[] }[]
  >([]);

  useEffect(() => {
    if (wizardVersion === WizardVersions.V5) {
      findDeparturesForWizard({
        matchingTrips: tripsToDisplay.map((trip) => ({ code: trip.code, score: 1 })),
        currency,
        region: language,
      }).then(setDepartures);
    }
  }, [tripsToDisplay, currency, language, wizardVersion]);

  const renderTripsDisplay = () => {
    if (tripsToDisplay.length === 0) {
      return (
        <WizardBanner
          size="large"
          openWizard={() => setIsWizardOpen(true)}
          sx={{ mt: isCompactView ? 0 : 2 }}
        />
      );
    }

    return (
      <>
        {!isCompactView && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <OpenWizardButton openWizard={() => setIsWizardOpen(true)} />
          </Box>
        )}
        <WizardTrackingProvider
          wizardTracking={wizardTracking}
          accessedResultsFrom={accessedFrom}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isCompactView ? 'column' : 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            {tripsToDisplay.map((trip, index) => (
              <WizardResultCard
                key={trip.code}
                trip={trip}
                departures={
                  departures.find((d) => d.tripCode === trip.code)?.departures || []
                }
                order={index}
                sx={{ maxWidth: '400px', width: '100%' }}
                shouldPersonalize={shouldPersonalize}
              />
            ))}
          </Box>
        </WizardTrackingProvider>
        {isCompactView && (
          <OpenWizardButton openWizard={() => setIsWizardOpen(true)} isMobile />
        )}
      </>
    );
  };

  return (
    <>
      <Wizard isOpen={isWizardOpen} setIsOpen={setIsWizardOpen} location="mytrips" />
      <Stack gap={isCompactView ? 3 : 1} pt={isCompactView ? 3 : 1} alignItems="center">
        {renderTripsDisplay()}
      </Stack>
    </>
  );
};

const tabData: TabData[] = [
  {
    label: 'Recently viewed',
    icon: <HistoryIcon />,
    component: <RecentlyViewedTrips />,
    hash: 'recently-viewed',
  },
  {
    label: 'Favorites',
    icon: <FavoriteBorderIcon />,
    component: <FavoriteTrips />,
    hash: 'favorites',
  },
  {
    label: 'Your recommendations',
    icon: <AutoAwesomeIcon />,
    component: <RecommendedTrips />,
    hash: 'recommendations',
  },
];

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`flashpack-tabpanel-${index}`}
    aria-labelledby={`flashpack-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pb: 8 }}>{children}</Box>}
  </div>
);

const FlashpackTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.principal.grey50}`,
  minHeight: '42px',
  '& .MuiTabs-flexContainer': {
    height: '100%',
  },
}));

const FlashpackTab = styled(Tab)(({ theme }) => ({
  minHeight: 'auto',
  border: 'none',
  color: theme.palette.principal.grey70,
  '&.Mui-selected': {
    color: theme.palette.principal.black,
  },
}));

const OpenWizardButton: React.FC<{ openWizard: () => void; isMobile?: boolean }> = ({
  openWizard,
  isMobile = false,
}) => {
  const title = 'Not quite right?';
  const buttonText = 'Refine my answers';

  if (isMobile) {
    return (
      <Stack gap="16px" sx={{ width: '100%', maxWidth: '400px', maxHeight: '850px' }}>
        <Typography variant="H5" textAlign="center">
          {title}
        </Typography>
        <FlashpackButton
          variant="secondary"
          fullWidth
          onClick={openWizard}
          endIcon={<ArrowForwardIcon />}
        >
          {buttonText}
        </FlashpackButton>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center">
      <Typography variant="Body M">{title}</Typography>
      <Button
        variant="text"
        disableRipple
        onClick={openWizard}
        sx={{
          textDecoration: 'underline',
          display: 'flex',
          gap: '4px',
          padding: '8px',
          '&:hover': {
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
        }}
      >
        {buttonText}
        <ArrowForwardIcon />
      </Button>
    </Stack>
  );
};

export default MyTrips;
